/**
 * Fix 2026-09-02 : dashboard « Matières critiques ».
 *
 * Objectif métier : identifier les matières où les étudiants ont le plus de
 * difficulté (moyennes basses + taux d'échec élevé) pour déclencher une action
 * pédagogique (renforcement, rappel au formateur, remise à plat du cours).
 *
 * Filtres : semestre impair/pair (ou tous), session ordinaire/rattrapage/toutes,
 * niveau, seuil échec.
 *
 * Design : sobre (palette gris/rouge d'alerte), tableau lisible, chip criticité
 * par ligne, mini-graphe barres taux d'échec, cohérent avec le reste du
 * dashboard (Card + Section existants dans Dashboard.tsx).
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  AlertTriangle, RefreshCw, TrendingDown, Loader2, BookOpen,
  Filter, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts';
import { statistiquesService } from '../../services/api.service';

interface Props {
  anneeAcademique?: string;
  pole?: string;
}

type Parite = 'impair' | 'pair' | 'toutes';
type Session = 'ordinaire' | 'rattrapage' | 'toutes';

type MatiereCritique = Awaited<ReturnType<typeof statistiquesService.getMatieresCritiques>>['matieres'][number];
type Repartition = Awaited<ReturnType<typeof statistiquesService.getMatieresCritiques>>['repartition_pole'][number];
type Response = Awaited<ReturnType<typeof statistiquesService.getMatieresCritiques>>;

const MatieresCritiquesSection: React.FC<Props> = ({ anneeAcademique, pole }) => {
  const [data, setData] = useState<Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [parite, setParite] = useState<Parite>('toutes');
  const [session, setSession] = useState<Session>('toutes');
  const [niveau, setNiveau] = useState<string>('');
  const [seuil, setSeuil] = useState<number>(10);
  const [expanded, setExpanded] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await statistiquesService.getMatieresCritiques({
        annee_academique: anneeAcademique,
        parite,
        session,
        niveau: niveau || undefined,
        pole,
        seuil_echec: seuil,
        limit: 30,
      });
      setData(res);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [anneeAcademique, pole, parite, session, niveau, seuil]);

  useEffect(() => { fetch(); }, [fetch]);

  const t = data?.totaux;

  // Top 8 matières pour le mini-chart
  const chartData = (data?.matieres || []).slice(0, 8).map(m => ({
    code: m.code_matiere,
    nom: m.nom_matiere.length > 18 ? m.nom_matiere.slice(0, 16) + '…' : m.nom_matiere,
    score: m.score_criticite,
  }));

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header pliable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <TrendingDown className="text-red-600 dark:text-red-400" size={18} />
          </div>
          <div className="text-left">
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
              Matières où les étudiants ont mal travaillé
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Aide à la décision pédagogique · taux d'échec ≥ {seuil}/20
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {t && !loading && (
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-semibold">
                <AlertTriangle size={10} /> {t.nb_critiques} critique{t.nb_critiques > 1 ? 's' : ''}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-semibold">
                {t.nb_surveiller} à surveiller
              </span>
            </div>
          )}
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>

      {!expanded && null}
      {expanded && (
        <>
          {/* Filtres */}
          <div className="p-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">
              <Filter size={11} /> Filtres
            </span>

            {/* Parité */}
            <div className="inline-flex items-center bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
              {([
                { v: 'toutes', l: 'Tous' },
                { v: 'impair', l: 'S impair' },
                { v: 'pair', l: 'S pair' },
              ] as const).map(o => (
                <button key={o.v}
                  onClick={() => setParite(o.v)}
                  className={`px-2.5 py-1 text-[11px] font-semibold transition ${
                    parite === o.v
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>

            {/* Session */}
            <div className="inline-flex items-center bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
              {([
                { v: 'toutes', l: 'Toutes sessions' },
                { v: 'ordinaire', l: 'Ordinaire' },
                { v: 'rattrapage', l: 'Rattrapage' },
              ] as const).map(o => (
                <button key={o.v}
                  onClick={() => setSession(o.v)}
                  className={`px-2.5 py-1 text-[11px] font-semibold transition ${
                    session === o.v
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>

            {/* Niveau */}
            <select value={niveau} onChange={e => setNiveau(e.target.value)}
              className="px-2 py-1 text-[11px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-gray-700 dark:text-gray-300 font-semibold">
              <option value="">Tous niveaux</option>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
              <option value="M1">M1</option>
              <option value="M2">M2</option>
            </select>

            {/* Seuil échec */}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
              Seuil échec
              <input type="number" min={5} max={15} step={0.5}
                value={seuil}
                onChange={e => setSeuil(parseFloat(e.target.value) || 10)}
                className="w-14 px-1.5 py-0.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-center font-semibold" />
              /20
            </label>

            <button onClick={fetch} disabled={loading}
              className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">
              <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>

          {err && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              {err}
            </div>
          )}

          {loading && (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
              <Loader2 size={16} className="inline animate-spin mr-2" /> Analyse en cours…
            </div>
          )}

          {!loading && data && (
            <>
              {/* Chart top matières critiques */}
              {chartData.length > 0 && (
                <div className="p-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-gray-600 dark:text-gray-400 mb-2">
                    Top 8 matières les plus critiques (score de criticité)
                  </div>
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="code"
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          angle={-40}
                          textAnchor="end"
                          interval={0}
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={[0, 100]} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', fontSize: 11 }}
                          formatter={(v: any, _n, p: any) => [
                            `${v} pts (${p.payload.nom})`,
                            'Criticité',
                          ]}
                        />
                        <Bar dataKey="score" radius={[3, 3, 0, 0]}>
                          {chartData.map((d, i) => (
                            <Cell key={i} fill={
                              d.score >= 55 ? '#dc2626'   // rouge
                              : d.score >= 30 ? '#f59e0b'  // amber
                              : '#64748b'                    // slate
                            } />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Tableau détaillé */}
              {data.matieres.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 text-[10px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 dark:bg-gray-800/60 z-10">Matière</th>
                        <th className="text-left px-2 py-2">Niveau · Option</th>
                        <th className="text-center px-2 py-2">Sem.</th>
                        <th className="text-center px-2 py-2">Étud.</th>
                        <th className="text-center px-2 py-2">Devoir</th>
                        <th className="text-center px-2 py-2">Examen</th>
                        {(session === 'rattrapage' || session === 'toutes') && (
                          <th className="text-center px-2 py-2">Rattrap.</th>
                        )}
                        <th className="text-center px-2 py-2">Criticité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.matieres.map(m => (
                        <LigneMatiere key={`${m.matiere_id}-${m.semestre}`} m={m} showRatt={session !== 'ordinaire'} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                  <BookOpen size={24} className="inline text-gray-300 mb-2" />
                  <div>Aucune matière critique détectée avec ces filtres.</div>
                  <div className="text-[11px] mt-1">Tous les étudiants s'en sortent — ou aucune note n'est encore saisie.</div>
                </div>
              )}

              {/* Répartition par pôle */}
              {data.repartition_pole.length > 0 && (
                <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-gray-600 dark:text-gray-400 mb-2">
                    Répartition des matières critiques par pôle
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.repartition_pole.map(p => (
                      <LignePole key={p.pole} p={p} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

function LigneMatiere({ m, showRatt }: { m: MatiereCritique; showRatt: boolean }) {
  return (
    <tr className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40">
      <td className="px-3 py-2 sticky left-0 bg-white dark:bg-gray-900 z-10">
        <div className="font-mono text-[10px] text-gray-500">{m.code_matiere}</div>
        <div className="text-gray-800 dark:text-gray-200 truncate max-w-[220px]">{m.nom_matiere}</div>
      </td>
      <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
        <div className="text-[10px] font-semibold">{m.niveau}</div>
        <div className="text-[10px] truncate max-w-[160px] text-gray-500">{m.option}</div>
      </td>
      <td className="text-center px-2 py-2 text-gray-600 dark:text-gray-400">{m.semestre}</td>
      <td className="text-center px-2 py-2 text-gray-600 dark:text-gray-400">{m.nb_etudiants}</td>

      <td className="text-center px-2 py-2">
        {m.nb_notes_devoir > 0 ? (
          <div className="flex flex-col items-center">
            <span className={`font-bold ${m.moy_devoir && m.moy_devoir < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {m.moy_devoir?.toFixed(2) || '—'}
            </span>
            <span className="text-[9px] text-gray-500">
              {m.taux_echec_devoir}% échec · n={m.nb_notes_devoir}
            </span>
          </div>
        ) : <span className="text-gray-300">—</span>}
      </td>
      <td className="text-center px-2 py-2">
        {m.nb_notes_examen > 0 ? (
          <div className="flex flex-col items-center">
            <span className={`font-bold ${m.moy_examen && m.moy_examen < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {m.moy_examen?.toFixed(2) || '—'}
            </span>
            <span className="text-[9px] text-gray-500">
              {m.taux_echec_examen}% échec · n={m.nb_notes_examen}
            </span>
          </div>
        ) : <span className="text-gray-300">—</span>}
      </td>
      {showRatt && (
        <td className="text-center px-2 py-2">
          {m.nb_notes_rattrapage > 0 ? (
            <div className="flex flex-col items-center">
              <span className={`font-bold ${m.moy_rattrapage && m.moy_rattrapage < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                {m.moy_rattrapage?.toFixed(2) || '—'}
              </span>
              <span className="text-[9px] text-gray-500">
                {m.taux_echec_rattrapage}% · n={m.nb_notes_rattrapage}
              </span>
            </div>
          ) : <span className="text-gray-300">—</span>}
        </td>
      )}
      <td className="text-center px-2 py-2">
        <ChipCriticite c={m.criticite} score={m.score_criticite} />
      </td>
    </tr>
  );
}

function ChipCriticite({ c, score }: { c: 'critique' | 'surveiller' | 'ok'; score: number }) {
  const cfg = {
    critique: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-700 dark:text-red-300',
      border: 'border-red-300 dark:border-red-800',
      label: 'Critique',
    },
    surveiller: {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      text: 'text-amber-700 dark:text-amber-300',
      border: 'border-amber-300 dark:border-amber-800',
      label: 'À surveiller',
    },
    ok: {
      bg: 'bg-slate-100 dark:bg-gray-800',
      text: 'text-slate-600 dark:text-gray-400',
      border: 'border-slate-200 dark:border-gray-700',
      label: 'OK',
    },
  }[c];
  return (
    <div className="inline-flex flex-col items-center gap-0.5">
      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
        {cfg.label}
      </span>
      <span className="text-[9px] text-gray-400 tabular-nums">{score}</span>
    </div>
  );
}

function LignePole({ p }: { p: Repartition }) {
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded p-2 min-w-[180px]">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold truncate">{p.pole}</div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-sm font-bold text-red-600 dark:text-red-400 tabular-nums">
          {p.nb_critiques}
        </span>
        <span className="text-[10px] text-gray-500">critique{p.nb_critiques > 1 ? 's' : ''}</span>
      </div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
        {p.nb_matieres} matière{p.nb_matieres > 1 ? 's' : ''} · score moyen {p.moy_score}
      </div>
    </div>
  );
}

export default MatieresCritiquesSection;
